import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MarkEmailReadRounded from "@mui/icons-material/MarkEmailReadRounded";
import { authClient } from "../../lib/auth-client";
import { AuthPageLayout, AuthStatusBadge } from "../auth/AuthPageLayout";
import {
  makeGlassButtonSx,
  makeGlassFieldSx,
  primaryButtonSx,
} from "../auth/authStyles";

const ForgotPasswordPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const glassFieldSx = makeGlassFieldSx(isDark);
  const glassButtonSx = makeGlassButtonSx(isDark);

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      // Always show success to prevent user enumeration
      // Better Auth handles the email sending on the backend
      await authClient.requestPasswordReset({
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      });

      setSuccess(true);
      setIsLoading(false);
    } catch (error: any) {
      // Even on error, show success to prevent user enumeration
      // Only show error if it's a rate limit or server error
      if (error?.message?.includes("rate") || error?.message?.includes("server")) {
        setError(error.message ?? "Too many requests. Please try again later.");
        setIsLoading(false);
      } else {
        // For other errors (like user not found), still show success
        setSuccess(true);
        setIsLoading(false);
      }
    }
  };

  if (success) {
    return (
      <AuthPageLayout
        contentKey="sent"
        topSlot={
          <AuthStatusBadge tone="success">
            <MarkEmailReadRounded sx={{ fontSize: 34 }} />
          </AuthStatusBadge>
        }
        headline="Check your email"
        supporting={`If an account exists for ${email}, a link to reset your password is on its way. Give it a minute, and check your spam folder too.`}
      >
        <Button fullWidth onClick={() => navigate("/sign-in?method=email")} sx={glassButtonSx}>
          Back to sign in
        </Button>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      contentKey="form"
      headline="Reset your password"
      supporting="Enter the email on your account and we'll send you a link to create a new password."
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={1.5}>
          <TextField
            fullWidth
            size="small"
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
            autoFocus
            sx={glassFieldSx}
          />
          <Box sx={{ height: 8 }} />
          <Button fullWidth type="submit" disabled={isLoading} sx={primaryButtonSx}>
            {isLoading && <CircularProgress size={24} color="inherit" />}
            {!isLoading && "Send reset link"}
          </Button>
          <Box sx={{ pt: 1 }}>
            <Button
              fullWidth
              type="button"
              onClick={() => navigate("/sign-in?method=email")}
              disabled={isLoading}
              sx={glassButtonSx}
            >
              Back to sign in
            </Button>
          </Box>
        </Stack>
      </form>
    </AuthPageLayout>
  );
};

export default ForgotPasswordPage;
